import type { Query, QueryClient } from '@tanstack/react-query'
import type { Operation } from '@trpc/client'
import { MAX_FLIGHT_QUERIES, SINGLE_FLIGHT_HEADER, type FlightEnvelope, type FlightRequest } from 'shared/single-flight'

/**
 * The client half of single-flight mutations: the mutation POST lists the queries on screen, the response carries
 * their fresh results, and the post-write revalidation skips what arrived. docs/notes/server/single-flight.md
 */

/** Header bytes the list may take. A query that does not fit is simply not listed: it refetches, as without flight. */
const MAX_HEADER_BYTES = 4096

/** Set on a mutation's `trpc.context` by {@link noRevalidate}: a write that revalidates nothing lists nothing. */
interface FlightContext {
  flight?: boolean
}

/** For writes that opt out of the post-write revalidation (loops, timers): no refetch, and no flight reads either. */
export const noRevalidate = {
  meta: { invalidates: false },
  trpc: { context: { flight: false } satisfies FlightContext },
} as const

// Header values are latin1: escape everything past printable ASCII, which is still valid JSON.
const asciiJson = (value: unknown): string =>
  JSON.stringify(value).replace(/[\u007f-￿]/g, (c) => `\\u${c.charCodeAt(0).toString(16).padStart(4, '0')}`)

/**
 * A tRPC key is `[['auth', 'getUser'], { type: 'query' | 'infinite', input? }]`. An infinite query is listed only
 * when its pages follow the app's one paging convention (`getNextPageParam: (page) => page.nextCursor`), which is
 * the walk the server repeats.
 */
function flightRequestOf(query: Query): FlightRequest | null {
  const [path, args] = query.queryKey
  if (!Array.isArray(path) || !path.every((segment) => typeof segment === 'string')) return null
  if (typeof args !== 'object' || args === null) return null
  const { input, type } = args as { input?: unknown; type?: unknown }
  if (type === 'query') return input === undefined ? [path.join('.')] : [path.join('.'), input]
  if (type !== 'infinite') return null
  const data = query.state.data as { pages?: unknown[]; pageParams?: unknown[] } | undefined
  const pages = data?.pages ?? []
  const pageParams = data?.pageParams ?? []
  const followsNextCursor = pages.every(
    (page, index) =>
      index === pages.length - 1 || (page as { nextCursor?: unknown } | null)?.nextCursor === pageParams[index + 1],
  )
  if (pages.length === 0 || pages.length !== pageParams.length || !followsNextCursor) return null
  return [path.join('.'), input ?? null, pages.length, { c: pageParams[0] }]
}

/** Procedure calls a request costs the server: one per page. */
const callsOf = (request: FlightRequest): number => (request.length === 4 ? request[2] : 1)

interface Listing {
  header: string
  queries: Query[]
}

/** On screen, settled and not refetching: a query mid-fetch is left to the invalidation, which cancels that fetch. */
function listFlight(client: QueryClient): Listing | null {
  const parts: string[] = []
  const queries: Query[] = []
  let bytes = 2
  let calls = 0
  for (const query of client.getQueryCache().getAll()) {
    if (!query.isActive() || query.state.status !== 'success' || query.state.fetchStatus !== 'idle') continue
    const request = flightRequestOf(query)
    if (request === null || calls + callsOf(request) > MAX_FLIGHT_QUERIES) continue
    const part = asciiJson(request)
    if (bytes + part.length + 1 > MAX_HEADER_BYTES) continue
    bytes += part.length + 1
    calls += callsOf(request)
    parts.push(part)
    queries.push(query)
  }
  return queries.length > 0 ? { header: `[${parts.join(',')}]`, queries } : null
}

interface MutationRequest {
  /** Another mutation request was in flight at some point during this one, so its reads may predate that write. */
  overlapped: boolean
}

const outstanding = new Set<MutationRequest>()
const listings = new Map<string, Listing>()

/** Query hashes the last flight seeded, claimed by the next successful mutation's revalidation. */
let seeded: Set<string> | null = null

/**
 * The mutation link's `headers` half: lists the queries on screen unless every op in the batch opted out. The
 * listing is kept by its header value, which is how {@link createFlightFetch} finds it again.
 */
export function flightHeaders(client: QueryClient, opList: readonly Operation[]): Record<string, string> {
  if (opList.every((op) => (op.context as FlightContext).flight === false)) return {}
  const listing = listFlight(client)
  if (listing === null) return {}
  listings.set(listing.header, listing)
  return { [SINGLE_FLIGHT_HEADER]: listing.header }
}

/** The mutation link's `fetch`: sends the listing, unwraps the envelope, seeds what came back. */
export function createFlightFetch(
  client: QueryClient,
  // Looked up per call, never captured: `installVersionCapture` wraps `window.fetch` after this module loads.
  transport: (input: RequestInfo | URL, init?: RequestInit) => Promise<Response> = (input, init) => fetch(input, init),
): (input: RequestInfo | URL, init?: RequestInit) => Promise<Response> {
  return async (input, init) => {
    const headers = new Headers(init?.headers)
    const listed = headers.get(SINGLE_FLIGHT_HEADER)
    const listing = listed === null ? undefined : listings.get(listed)
    if (listed !== null) listings.delete(listed)

    const request: MutationRequest = { overlapped: outstanding.size > 0 }
    for (const other of outstanding) other.overlapped = true
    outstanding.add(request)
    // Overlapping from the start: the reads could run before the other write lands, so do not ask for them.
    if (request.overlapped) headers.delete(SINGLE_FLIGHT_HEADER)

    try {
      const response = await transport(input, { ...init, headers })
      if (response.headers.get(SINGLE_FLIGHT_HEADER) !== '1') return response
      const envelope = (await response.json()) as FlightEnvelope
      if (listing !== undefined && !request.overlapped) seed(client, listing.queries, envelope.flight)
      return new Response(JSON.stringify(envelope.batch), {
        status: response.status,
        statusText: response.statusText,
        headers: response.headers,
      })
    } finally {
      outstanding.delete(request)
    }
  }
}

function seed(client: QueryClient, queries: Query[], results: FlightEnvelope['flight']): void {
  const hashes = new Set<string>()
  queries.forEach((query, index) => {
    const result = results[index]
    // A fetch that started while the write was in flight may carry pre-write data; the invalidation cancels it.
    if (result == null || query.state.fetchStatus !== 'idle') return
    client.setQueryData(query.queryKey, result.d)
    hashes.add(query.queryHash)
  })
  seeded = hashes.size > 0 ? hashes : null
}

/**
 * The queries the last flight already refreshed, for the revalidation to skip, and only once. Claimed by the
 * MutationCache's `onSuccess`, which runs before any later response can seed again: overlapping requests never seed.
 */
export function claimFlightSeeded(): Set<string> | null {
  const claimed = seeded
  seeded = null
  return claimed
}
